/**
 * components/viewer/TileManager.jsx — raster source manager.
 *
 * Today's job: thinly wrap the dpr=1 → dpr=2 → dpr=4 escalation logic
 * already in SheetViewer.jsx so it has a stable home + a clean API surface.
 *
 * Tomorrow's job: drop in the true multi-resolution tile pyramid without
 * any consumer of `useTileManager` having to change. The consumer reads
 * `{ rasterUrl, ready, escalateForZoom }` and that contract stays.
 *
 * Why a "Manager" hook instead of a component: the consumer is already
 * inside the SheetCanvas render tree and just needs a URL + lifecycle
 * signals — wrapping it in a component would force a portal/context dance
 * the existing canvas doesn't need yet.
 */
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { sheetRasterUrl } from "@/lib/api";

/**
 * useTileManager — returns the raster URL for the current sheet at the
 * appropriate dpr for the current zoom level, plus an escalator the
 * viewport-pan-zoom callback can call.
 *
 * Behaviour (matches current SheetViewer logic):
 *   - boot     → dpr=1 (snappy first paint)
 *   - +700ms idle on retina → dpr=2
 *   - zoom past 1.8× fitScale → dpr=4
 *   - zoom back below 1.3× fitScale → demote to dpr=2 (or 1 if non-retina)
 *
 * 180ms debounce on escalation prevents flicker during a fast zoom gesture.
 */
export function useTileManager({ sheetId, isRetina = false, enabled = true }) {
  const [activeDpr, setActiveDpr] = useState(1);
  const escalateAtRef = useRef(0);

  // Reset on sheet change so first paint is always snappy.
  useEffect(() => { setActiveDpr(1); }, [sheetId]);

  // 700ms idle → retina sharpen to dpr=2.
  useEffect(() => {
    if (!enabled || !isRetina || activeDpr !== 1) return;
    const t = setTimeout(() => setActiveDpr(2), 700);
    return () => clearTimeout(t);
  }, [sheetId, activeDpr, isRetina, enabled]);

  /**
   * Call from the canvas's onPanZoom callback with {scale, fitScale}.
   * Implements hysteresis so escalation doesn't flicker around the threshold.
   */
  const escalateForZoom = useCallback(({ scale, fitScale }) => {
    if (!fitScale) return;
    const ratio = scale / fitScale;
    const now = Date.now();
    if (ratio >= 1.8 && activeDpr < 4) {
      if (now - escalateAtRef.current < 180) return;
      escalateAtRef.current = now;
      setActiveDpr(4);
    } else if (ratio < 1.3 && activeDpr === 4) {
      setActiveDpr(isRetina ? 2 : 1);
    }
  }, [activeDpr, isRetina]);

  const rasterUrl = useMemo(
    () => sheetId ? sheetRasterUrl(sheetId, { dpr: activeDpr }) : null,
    [sheetId, activeDpr],
  );

  return { rasterUrl, activeDpr, escalateForZoom };
}

/**
 * Default-export — placeholder component so the file is also importable as
 * a JSX element. Today it renders nothing; tomorrow the tile pyramid will
 * render <img> + <canvas> tile layers from here.
 */
export default function TileManager() {
  return null;
}
